/**
 * Vendor Edit Modal Validation Schemas
 * Zod schemas for vendor edit modals
 */

import { z } from 'zod';

/**
 * Edit Basic Info Modal Schema
 */
export const editBasicInfoSchema = z.object({
  // Contact Details
  phoneCountryCode: z.string().min(1, 'vendor.validation.countryCodeRequired'),
  phoneNumber: z
    .string()
    .min(1, 'vendor.validation.phoneRequired')
    .regex(/^\d{6,15}$/, 'vendor.validation.phoneFormat'),
  phoneExtension: z.string().optional().or(z.literal('')),
  email: z
    .string()
    .min(1, 'vendor.validation.emailRequired')
    .email('vendor.validation.emailInvalidFormat')
    .max(100, 'vendor.validation.emailMax'),
  web: z.string().optional().or(z.literal('')),
  fax: z.string().optional().or(z.literal('')),

  // Registered Location
  stateId: z.number().nullable(),
  stateName: z.string(),
  cityId: z.number().nullable(),
  cityName: z.string(),
  postcode: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
});

/**
 * Edit Financial Info Modal Schema
 */
export const editFinancialInfoSchema = z.object({
  // Payment Terms - Deposit & Cash Discount (always visible)
  deposit: z
    .number()
    .min(0, 'vendor.validation.depositMin')
    .max(100, 'vendor.validation.depositMax')
    .optional(),
  cashDiscount: z
    .number()
    .min(0, 'vendor.validation.cashDiscountMin')
    .max(100, 'vendor.validation.cashDiscountMax')
    .optional(),
  // Cash Discount Rate/Days (conditionally visible based on payment term hasDiscount)
  cashDiscountRate: z
    .number()
    .min(0, 'vendor.validation.cashDiscountMin')
    .max(100, 'vendor.validation.cashDiscountMax')
    .optional(),
  cashDiscountDays: z
    .number()
    .int()
    .min(0, 'vendor.validation.discountDaysMin')
    .optional(),
  termsTypeId: z.number().int().positive('vendor.validation.paymentTermsRequired'),
  termsDays: z.number().int().min(0, 'vendor.validation.termsDaysMin'),

  // Currency
  primaryCurrency: z
    .string()
    .length(3, 'vendor.validation.currencyFormat')
    .min(1, 'vendor.validation.currencyRequired'),

  // Tax Information
  abn: z.string().optional().or(z.literal('')),
  taxTypeId: z.number().int().positive('vendor.validation.taxTypeRequired'),
  taxRate: z
    .number()
    .min(0, 'vendor.validation.taxRateMin')
    .max(100, 'vendor.validation.taxRateMax'),
});

/**
 * Edit Vendor Header Modal Schema
 */
export const editVendorHeaderSchema = z.object({
  name: z
    .string()
    .min(1, 'vendor.validation.vendorNameRequired')
    .max(200, 'vendor.validation.vendorNameMax'),
  alternativeName: z
    .string()
    .max(200, 'vendor.validation.alternativeNameMax')
    .optional()
    .or(z.literal('')),
  nickname: z
    .string()
    .max(100, 'vendor.validation.nicknameMax')
    .optional()
    .or(z.literal('')),
  type: z.enum(['company', 'individual'], {
    required_error: 'vendor.validation.vendorTypeRequired',
  }),
  categoryIds: z.array(z.number()).min(1, 'vendor.validation.categoryRequired'),
  notes: z
    .string()
    .max(1000, 'vendor.validation.notesMax')
    .optional()
    .or(z.literal('')),
});

/**
 * Type exports
 */
export type EditBasicInfoFormData = z.infer<typeof editBasicInfoSchema>;
export type EditFinancialInfoFormData = z.infer<typeof editFinancialInfoSchema>;
export type EditVendorHeaderFormData = z.infer<typeof editVendorHeaderSchema>;
